// seatBlock — สวิตช์ปิดเครื่องจากหลังบ้าน (ย้ายมาจาก core/plugins/deviceBlock.client.ts
// ให้อยู่กลุ่มเดียวกับระบบที่นั่ง: seatBlock → seatRequest → seatMain → seatSecond)
//
// กันเครื่องที่ตั้งโหมด main cashier ผิดโดยไม่รู้ตัว / เครื่องที่แกล้งแย่งที่นั่ง
// (หน้า *-is-taken โชว์ Computer ID คนที่แย่ง ใช้ id นั้นบล็อกได้เลย)
//
// ปัญหาที่แก้: run mode เก็บอยู่ใน localStorage ของแต่ละเครื่อง ไม่มี lock ฝั่ง server
// เครื่องที่แอบเป็น main cashier ซ้ำจะทำให้ job อัปเดตซ้ำซ้อน / sync ไม่ตรง / พิมพ์ซ้ำ
// support จึงต้องมีวิธีสั่งหยุดเครื่องนั้นทันทีโดยไม่ต้องเดินไปที่ร้าน
//
// วิธีใช้ (ทำด้วยมือที่ Firebase Console เท่านั้น) ที่ /stores/{storeId}/blocked/{computerUuid}
//   true                                   → บล็อก ไม่มีเหตุผล ไม่มีปุ่มเปลี่ยนโหมด
//                                            (ค่าเริ่มต้น: ลูกค้าต้องโทรหา IT ให้อธิบาย)
//   "ข้อความ"                               → บล็อก + โชว์ข้อความเป็นเหตุผล
//   { reason?, allowChangeMode?, blockedAt? } → บล็อก + เหตุผล ถ้า allowChangeMode: true
//                                            หน้า blocked จะมีปุ่มให้ร้านเลือกโหมดใหม่เอง
//                                            (ใส่ blocked: false ใน object = ไม่บล็อก)
//   ลบ node ทิ้ง / false / null              → ปกติ
//
// !! ไฟล์นี้ห้ามเขียน RTDB — ข้อยกเว้นเดียวคือ remove() node blocked ของ "เครื่องตัวเอง"
//    หลังร้านกดปุ่มเปลี่ยนโหมดในหน้า blocked (support อนุญาตไว้ด้วย allowChangeMode)
//
// ลำดับ boot: ตัวนี้เป็น 'pre' ต่อจาก computerIdentity (ก่อน runModeMigration)
// ซึ่ง resolve storeId ไว้แล้ว
// (resolveStoreId() memoize ไว้ ไม่ได้ยิง /users/{uid} ซ้ำ)
import "../../../firebaseInstance";
import { getDatabase, ref, onValue, get, remove } from "firebase/database";
import * as Sentry from "@sentry/vue";
import type { NuxtApp } from "#app";
import { resolveStoreId } from "../../core/composables/useStoreIdentity";
import { useRunningMode } from "../../core/composables/useRunningMode";
import {
  DEVICE_BLOCKED_KEY,
  PENDING_SELF_UNBLOCK_KEY,
  SEAT_PAGES,
  SUPPORT_UNBLOCK_KEY,
} from "../utils/seatStorage";

// DEVICE_BLOCKED_KEY ("deviceBlocked") / PENDING_SELF_UNBLOCK_KEY ("pendingSelfUnblock")
// ย้ายไปอยู่ที่ utils/seatStorage.ts ใช้ชื่อ key เดิมทุกตัว
/** static page ล้วน ๆ ใน public/seat/ — ไม่ผ่าน Nuxt router / ไม่โหลด bundle แอปเลย */
const BLOCKED_PATH = SEAT_PAGES.blocked;
/** ปลดเองค้างเกินนี้ทิ้งไปเลย — กันไปลบ block รอบใหม่ที่ support ตั้งทีหลัง */
const SELF_UNBLOCK_TTL_MS = 24 * 60 * 60 * 1000;
/** RTDB get/remove ตอนออฟไลน์จะค้างไม่ reject — boot ห้ามรอนานกว่านี้ */
const SELF_UNBLOCK_TIMEOUT_MS = 8000;

/** ใช้แค่ hooks ไว้เลื่อนยิง Sentry (setup() ได้ _NuxtApp ไม่ใช่ NuxtApp ตรง ๆ) */
type HookableApp = Pick<NuxtApp, "hooks">;

export interface DeviceBlockState {
  blocked: boolean;
  reason: string;
  allowChangeMode: boolean;
  blockedAt: string;
}

/**
 * แปลงค่าใน /stores/{storeId}/blocked/{computerUuid} ให้เป็นรูปเดียว
 * รองรับ true / "เหตุผล" / { reason, allowChangeMode, blockedAt } (ดูหัวไฟล์)
 * ค่าแปลก ๆ อื่นนอกจากนี้ถือว่าไม่บล็อก
 */
export function parseBlock(val: unknown): DeviceBlockState {
  const state: DeviceBlockState = {
    blocked: false,
    reason: "",
    allowChangeMode: false,
    blockedAt: "",
  };

  if (val === true) {
    state.blocked = true;
  } else if (typeof val === "string") {
    state.reason = val.trim();
    state.blocked = state.reason !== "";
  } else if (val && typeof val === "object" && !Array.isArray(val)) {
    const obj = val as Record<string, unknown>;
    state.blocked = obj.blocked !== false;
    state.reason = typeof obj.reason === "string" ? obj.reason.trim() : "";
    // ต้องเป็น true จริง ๆ เท่านั้น — "true" / 1 ไม่นับ กันพิมพ์ผิดใน Console
    state.allowChangeMode = obj.allowChangeMode === true;
    state.blockedAt = typeof obj.blockedAt === "string" ? obj.blockedAt : "";
  }

  if (!state.blocked) state.allowChangeMode = false;
  return state;
}

export default defineNuxtPlugin({
  name: "seat-block",
  enforce: "pre",
  parallel: false,
  async setup(nuxtApp) {
    try {
      // ---- ร้านเพิ่งกด "เปลี่ยนโหมดใช้งาน" ในหน้า blocked -------------------
      // ต้องทำก่อน fast path และก่อนต่อ listener — ไม่งั้น listener จะเห็น node
      // ยังเป็น block อยู่แล้วเด้งกลับไปหน้า blocked ทันที วนไม่จบ
      //
      // อีกทางคือ IT support ปลดผ่านรหัสลับในหน้า blocked (แตะชื่อร้าน 5 ครั้ง + "POS")
      // หน้า static ไม่มี firebase จึงฝาก flag supportUnblock ไว้ให้ตรงนี้ลบ node ให้
      let skipListener = false;
      for (const kind of ["support", "self"] as const) {
        if (!safeGet(unblockKey(kind))) continue;
        if ((await processUnblock(nuxtApp, kind)) === "retry") skipListener = true;
      }

      // ---- fast path: เคยโดนบล็อกแล้ว เด้งเลยโดยไม่แตะ network -------------
      // hard redirect ไปหน้า static ไม่ใช่ router.push — เครื่องที่ถูกบล็อกจะได้
      // ไม่รันแอปต่อแม้แต่วินาทีเดียว และหน้าปลายทางก็ไม่ต้องโหลด bundle อะไรเลย
      if (safeGet(DEVICE_BLOCKED_KEY)) {
        window.location.replace(BLOCKED_PATH);
        return;
      }

      // ลบ node ไม่สำเร็จ (ส่วนใหญ่คือเน็ตหลุด) — รอบนี้ไม่ต่อ listener ปล่อยให้ร้าน
      // ไปเลือกโหมดได้ก่อน แล้ว boot รอบหน้าค่อยลองลบใหม่ (flag ยังเก็บไว้)
      if (skipListener) return;

      // อ่านแบบไม่สร้างใหม่ — เครื่องที่ยังไม่เคย login จะยังไม่มี computerUuid
      // (auth store เป็นคนสร้างด้วย nanoid(6)) เครื่องแบบนั้นยังไม่มีทางถูกบล็อก
      const computerId = safeGet("computerUuid");
      if (!computerId) return;

      const storeId = await resolveStoreId();
      if (!storeId) return; // ยังไม่ login

      // ไม่ await ค่าแรก — boot ต้องไม่ช้าตามเน็ต
      // listener อยู่ยาวตลอด session เพื่อให้ support กด block แล้วเครื่องที่เปิด
      // ค้างอยู่หยุดทันที ไม่ต้องรอให้ปิดเปิดแอปใหม่
      const db = getDatabase();
      onValue(
        ref(db, `stores/${storeId}/blocked/${computerId}`),
        (snapshot) => {
          const block = parseBlock(snapshot.val());
          if (!block.blocked) return;
          void applyBlock(storeId, computerId, block);
        },
        (error) => {
          console.warn("[deviceBlock] อ่าน node blocked ไม่ได้", error);
        }
      );
    } catch (error) {
      // พังยังไงก็ห้ามทำให้แอปเปิดไม่ขึ้น
      console.warn("[deviceBlock] ข้ามการเช็คบล็อก", error);
    }
  },
});

type UnblockKind = "self" | "support";

const unblockKey = (kind: UnblockKind) =>
  kind === "self" ? PENDING_SELF_UNBLOCK_KEY : SUPPORT_UNBLOCK_KEY;

/**
 * ลบ node blocked ของเครื่องนี้ตามที่หน้า blocked.html ฝาก flag ไว้
 *
 *   self    = ร้านกดปุ่มเปลี่ยนโหมด (มีเฉพาะเมื่อ support ตั้ง allowChangeMode)
 *             หน้านั้นล้าง deviceBlocked + run mode ไว้ให้แล้ว เข้าแอปมาเป็น UNASSIGNED
 *             ลบเฉพาะตอนที่ support ยังอนุญาตอยู่จริง
 *   support = IT support ใส่รหัสลับ (แตะชื่อร้าน 5 ครั้ง + "POS") — ปลดได้ทุกกรณี
 *             หน้านั้นล้างโหมดเป็น "ยังไม่เลือก" + ฝาก seatChangeRequest ไว้ให้
 *             seatRequest คืนที่นั่งต่อ (แบบเดียวกับปุ่มเปลี่ยนโหมด)
 *
 * @returns "retry" เมื่อคุยกับ RTDB ไม่สำเร็จ — เก็บ flag ไว้ลองใหม่ boot หน้า
 *          และรอบนี้ห้ามต่อ listener (node ยังเป็น block อยู่ จะเด้งกลับทันที)
 */
async function processUnblock(
  nuxtApp: HookableApp,
  kind: UnblockKind
): Promise<"done" | "skip" | "retry"> {
  const key = unblockKey(kind);
  let pending: { computerId?: string; storeId?: string; at?: string } = {};
  try {
    pending = JSON.parse(safeGet(key) || "{}") || {};
  } catch {
    // flag เพี้ยน — ทิ้งไป ไม่รู้ว่าของเครื่อง/ร้านไหน
  }

  const computerId = safeGet("computerUuid");
  const storeId = await resolveStoreId();
  const extra = {
    storeId,
    computerId,
    pendingStoreId: pending.storeId,
    pendingComputerId: pending.computerId,
    pendingAt: pending.at,
  };

  // ยังไม่ login / ยังไม่มี computer id — เก็บ flag ไว้ก่อน รอ login แล้ว boot ใหม่
  if (!computerId || !storeId) return "skip";

  const at = Date.parse(pending.at || "");
  if (!pending.computerId || !pending.storeId || isNaN(at)) {
    safeRemove(key);
    return "skip";
  }

  // ค้างนานเกิน — ทิ้ง ไม่ลบ node (support อาจบล็อกรอบใหม่ไปแล้ว)
  if (Date.now() - at > SELF_UNBLOCK_TTL_MS) {
    safeRemove(key);
    reportSelfUnblock(nuxtApp, kind, "expired", "warning", extra);
    return "skip";
  }

  // flag ของร้าน/เครื่องอื่น (เช่น sign in สลับร้าน) — ไม่ยุ่ง เก็บไว้ให้ร้านนั้น
  if (pending.computerId !== computerId || pending.storeId !== storeId) {
    return "skip";
  }

  const nodeRef = ref(
    getDatabase(),
    `stores/${storeId}/blocked/${computerId}`
  );
  try {
    // self: อ่านค่าปัจจุบันก่อน — ลบเฉพาะตอนที่ support ยังอนุญาตอยู่จริง
    // (ถ้า support เปลี่ยนเป็น true เฉย ๆ ไปแล้ว ต้องโทรคุย ห้ามปลดเอง)
    // support: ใส่รหัสลับถูกแล้ว ปลดได้ทุกกรณี
    const snapshot = await withTimeout(get(nodeRef), SELF_UNBLOCK_TIMEOUT_MS);
    const block = parseBlock(snapshot.val());

    if (kind === "self" && block.blocked && !block.allowChangeMode) {
      safeRemove(key);
      reportSelfUnblock(nuxtApp, kind, "not_allowed", "warning", extra);
      return "skip"; // listener จะบล็อกกลับเองตามปกติ
    }

    // await ให้ลบเสร็จก่อนต่อ listener — กัน race ที่ listener เห็นค่าเก่าแล้วบล็อกซ้ำ
    if (block.blocked) {
      await withTimeout(remove(nodeRef), SELF_UNBLOCK_TIMEOUT_MS);
    }

    safeRemove(key);
    console.info(`[seatBlock] ปลดบล็อก (${kind}) สำเร็จ`, extra);
    reportSelfUnblock(
      nuxtApp,
      kind,
      block.blocked ? "unblocked" : "already_clear",
      "info",
      extra
    );
    return "done";
  } catch (error) {
    console.warn(
      "[seatBlock] ลบ node blocked ไม่สำเร็จ จะลองใหม่ตอนเปิดแอปครั้งหน้า",
      error
    );
    reportSelfUnblock(nuxtApp, kind, "remove_failed", "warning", {
      ...extra,
      error: String(error),
    });
    return "retry";
  }
}

/**
 * Sentry ยังไม่ init ตอน plugin นี้รัน (sentry.client ไม่ใช่ 'pre')
 * เลยเลื่อนไปยิงตอน app:mounted ซึ่ง plugin ทุกตัวรันครบแล้ว
 */
function reportSelfUnblock(
  nuxtApp: HookableApp,
  kind: UnblockKind,
  outcome: string,
  level: "info" | "warning",
  extra: Record<string, unknown>
) {
  nuxtApp.hooks.hookOnce("app:mounted", () => {
    try {
      Sentry.addBreadcrumb({
        category: "deviceBlock",
        message: `${kind} unblock: ${outcome}`,
        level,
        data: extra,
      });
      Sentry.captureMessage(`[seatBlock] ${kind} unblock ${outcome}`, {
        level,
        extra,
      });
    } catch {
      // Sentry ไม่ได้ init (dev / localhost)
    }
  });
}

async function applyBlock(
  storeId: string,
  computerId: string,
  block: DeviceBlockState
) {
  let shopName = "";
  try {
    const snapshot = await get(ref(getDatabase(), `stores/${storeId}/_name`));
    shopName = snapshot.val() || "";
  } catch {
    // ไม่มีชื่อร้านก็ยังบล็อกต่อ — support ใช้ storeId แทนได้
  }

  // เขียนเหตุผล / allowChangeMode ไว้ครั้งเดียวตอนบล็อก หน้า blocked.html
  // ไม่คุยกับ server เลย (ไม่มี firebase) อ่านจาก key นี้อย่างเดียว
  const blockedAt = isNaN(Date.parse(block.blockedAt))
    ? new Date().toISOString()
    : new Date(block.blockedAt).toISOString();

  safeSet(
    DEVICE_BLOCKED_KEY,
    JSON.stringify({
      computerId,
      storeId,
      shopName,
      runMode: useRunningMode().getMode(),
      blockedAt,
      reason: block.reason,
      allowChangeMode: block.allowChangeMode,
    })
  );

  // reload จริง ไม่ใช่ router.push — ต้องฆ่า RTDB listener / คิวพิมพ์ / pinia
  // subscriber ของเครื่องที่กำลังทำงานผิดอยู่ให้หมด ไม่งั้นมันยิง print ซ้ำต่อ
  // ทั้งที่หน้าจอขึ้นว่าถูกบล็อกแล้ว
  window.location.replace(BLOCKED_PATH);
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`timeout ${ms}ms (offline?)`)),
      ms
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // private mode / quota เต็ม — รอบนี้ยังบล็อกได้ แต่ reload หน้าถัดไปจะต้อง
    // ไปเช็คออนไลน์ใหม่ (ซึ่งก็ยังบล็อกอยู่ดีถ้า node ยังเป็น block)
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {}
}
